import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/types';
import { getAGBLink, getPartnerText } from '../../../utils/getter';
import './footer.css';
import { parseLegalDescription } from './parse-legal-description';

interface FooterProps {
  className?: string;
}

interface FooterLink {
  label: string;
  href: string;
  testId?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

/*
 * Responsive layout (Figma: Footer/Mobile, Footer/Tablet, Footer/Desktop),
 * mobile-first with the project breakpoints: base < 640px, md >= 640px,
 * lg >= 899px (see src/styles/breakpoints.ts).
 *
 * Global `a`/`p` rules and the typography classes are unlayered CSS, so
 * Tailwind overrides on top of them need the important modifier.
 */
const whiteText = 'text-(--vel-color-neutral-white,#fff)!';
const contactLinkClass = `${whiteText} after:hidden! hover:underline!`;
const footerLinkClass = `alt-text-tight-100-bold block whitespace-nowrap ${whiteText} underline! decoration-solid decoration-from-font underline-offset-auto after:hidden! hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--vel-color-neutral-white,#fff)`;
// Mobile: alt-text-tight-200-bold (15/20) -> tablet+: alt-text-tight-300-bold (17/24)
const headingClass = `alt-text-tight-200-bold md:text-alt-300!`;
// Mobile: text-loose-100 (14/24) -> tablet+: text-loose-300 (18/28)
const contactTextClass = `text-loose-100 md:text-body-loose-300!`;
/*
 * Tablet+: links flow in wrapping rows with a 1x24px separator 12px left of
 * every item. The first item of each row puts its separator outside the
 * clipping wrapper, so separators only show *between* links on every row.
 */
const linkItemClass = `relative flex h-5 items-center md:h-6 md:before:absolute md:before:top-0 md:before:-left-[13px] md:before:h-6 md:before:w-px md:before:bg-(--vel-color-secondary-300,#3f5d6d)`;

const Footer: React.FC<FooterProps> = ({ className = '' }) => {
  // Redux Store
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  const contactNumber = getPartnerText('contact_us_number');
  const contactEmail = getPartnerText('contact_us_email');
  const footerUrl = getPartnerText('footer_url');
  const legalSections = parseLegalDescription(
    String(getPartnerText('bottom_description') ?? ''),
  );

  const links: FooterLink[] = [
    {
      label: 'Datenschutzhinweise Website',
      href: 'https://www.ald-online.de/pages/datenschutz',
      testId: 'datenschutz',
    },
    {
      label: 'Datenschutzhinweise für Kunden',
      href: `${getPartnerText('datenschutzhinweise_link')}`,
      testId: 'datenschutz-kunden',
    },
    {
      label: getPartnerText('agb_text'),
      href: getAGBLink(activeApplication),
      testId: 'agb',
    },
    {
      label: 'Privatsphäre-Einstellungen',
      href: '#/',
      testId: 'privacy-settings',
      onClick: (event) => {
        event.preventDefault();
        const browserWindow: any = window;
        if (browserWindow.UC_UI) {
          browserWindow.UC_UI.showSecondLayer();
        }
      },
    },
    {
      label: 'Impressum',
      href: 'https://www.ald-online.de/pages/impressum',
      testId: 'impressum',
    },
    {
      label: 'Whistleblowing',
      href: 'https://report.whistleb.com/de/AyvensDE',
      testId: 'whistleblowing',
    },
    {
      label: 'Verhaltenskodex',
      href: 'https://mobility.ayvens.de/pages/Verhaltenskodex',
      testId: 'verhaltenskodex',
    },
    {
      label: 'Société Générale',
      href: 'https://www.societegenerale.com/en',
      testId: 'societe-generale',
    },
  ];

  return (
    <footer
      className={`footer bg-(--vel-color-secondary-400,#0f3549) px-4 pt-7 pb-6 text-left ${whiteText} md:px-12 md:pt-12 md:pb-12 lg:pb-5 ${className}`.trim()}
      data-testid='footer'
    >
      {/* Contact / business hours */}
      <section aria-label='Kontakt' data-testid='footer-contact'>
        <p className={`${headingClass} m-0! ${whiteText}`}>Geschäftszeiten</p>
        <ul
          className={`${contactTextClass} mt-1 mb-0 list-none p-0 md:mt-0.75 ${whiteText}`}
        >
          <li>{getPartnerText('opening_hours')}</li>
          <li>
            Tel.:{' '}
            <a className={contactLinkClass} href={`tel:${contactNumber}`}>
              {contactNumber}
            </a>
          </li>
          <li>
            <a className={contactLinkClass} href={`mailto:${contactEmail}`}>
              {contactEmail}
            </a>
          </li>
          <li>
            <a
              className={contactLinkClass}
              href={`https://${footerUrl}/`}
              target='_blank'
              rel='noopener noreferrer'
            >
              {footerUrl}
            </a>
          </li>
        </ul>
      </section>

      {/* Legal links */}
      <nav aria-label='Rechtliche Hinweise' className='mt-6.75 md:mt-8.5'>
        {/* 4px inset keeps focus outlines visible while clipping row-start separators */}
        <div className='-m-1 overflow-hidden p-1'>
          <ul
            className='m-0 flex list-none flex-col items-start gap-y-2 p-0 md:flex-row md:flex-wrap md:gap-x-6.25 md:gap-y-2.5'
            data-testid='footer-links'
          >
            {links.map((link) => (
              <li key={link.testId ?? link.label} className={linkItemClass}>
                <a
                  className={footerLinkClass}
                  data-testid={link.testId}
                  href={link.href}
                  onClick={link.onClick}
                  {...(link.onClick
                    ? {}
                    : { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <p className={`text-loose-100 mt-6! mb-0! md:mt-5.5! ${whiteText}`}>
        © {new Date().getFullYear()} Ayvens
      </p>

      {/* Company / legal information */}
      {legalSections.length > 0 && (
        <div
          className={`text-loose-100 mt-12 flex flex-col gap-6 lg:mt-6 ${whiteText}`}
          data-testid='footer-legal'
        >
          {legalSections.map((section, sectionIndex) => (
            <div key={`${sectionIndex}-${section.heading}`}>
              <p className={`text-loose-100-bold m-0! ${whiteText}`}>
                {section.heading}
              </p>
              {section.lines.map((line, lineIndex) => (
                <p key={`${lineIndex}-${line}`} className={`m-0! ${whiteText}`}>
                  {line}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
    </footer>
  );
};

export default Footer;
